import { QueryCache, QueryClient } from '@tanstack/react-query'
import getLogger from 'utils/log/log'
import { captureAlarm } from 'utils/sentry/alarm'
import type { TerminalErrorQuery } from './queryClient.types'

const log = getLogger('queryClient')

// Number of retry attempts for failed queries
const MAX_RETRY_ATTEMPTS = 3

export function getErrorStatus(error: unknown): number | undefined {
  return error instanceof Error && 'status' in error ? (error as { status?: number }).status : undefined
}

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRY_ATTEMPTS) {
    return false
  }

  const status = getErrorStatus(error)
  if (status === undefined) {
    return true
  }

  if (status === 408 || status === 429) {
    return true
  }

  if (status >= 400 && status < 500) {
    return false
  }

  return true
}

/**
 * Returns retry delay in milliseconds.
 */
export function getQueryRetryDelay(attemptIndex: number): number {
  return Math.min(500 * 2 ** attemptIndex, 2000)
}

/**
 * A terminal 404 is expected "not found" noise for most queries, but some resources are known to
 * exist. Those queries can opt in by setting `meta.terminalErrorIssueType`, which returns a Sentry
 * `issue_type` for alarming once retries are exhausted.
 */
export function getTerminalErrorAlarmIssueType(query: TerminalErrorQuery, error: unknown): string | undefined {
  if (query.state.fetchFailureCount < MAX_RETRY_ATTEMPTS) {
    return undefined
  }

  if (getErrorStatus(error) !== 404) {
    return undefined
  }

  const issueType = query.meta?.terminalErrorIssueType
  return typeof issueType === 'string' ? issueType : undefined
}

/**
 * Global QueryClient Configuration
 *
 * Singleton QueryClient instance shared throughout the application, providing centralized
 * configuration for all React Query operations (useQuery, useMutation, etc.).
 *
 * **Setup**: Provided to the app via `<QueryClientProvider>` in `AllProviders.tsx`,
 * making it available to all components. The singleton is created at module load before
 * any components render, eliminating race conditions.
 *
 * **404 Handling**: Most 404 errors are treated as expected "not found" noise and are not retried
 * or sent to Sentry. Queries for eventually consistent resources can opt in to terminal alarming by
 * setting `meta.terminalErrorIssueType` (see example below).
 *
 * ## Integration with useQuery
 *
 * When you call useQuery() anywhere in the app, it automatically inherits these global defaults:
 *
 * ```typescript
 * // This query inherits: retry: 3, retryDelay, gcTime, staleTime, refetchOnWindowFocus: false
 * const { data } = useQuery({
 *   queryKey: ['users'],
 *   queryFn: fetchUsers,
 * })
 * ```
 *
 * Eventual-consistency opt-in for terminal 404 alarms:
 *
 * ```typescript
 * const { data } = useQuery({
 *   queryKey: ['digitalTwin', uid],
 *   queryFn: fetchDigitalTwin,
 *   meta: {
 *     terminalErrorIssueType: 'digital_twin_replication_timeout',
 *   },
 * })
 * ```
 *
 * Individual queries can override global defaults:
 *
 * ```typescript
 * // This overrides the global retry: 3 setting
 * const { data } = useQuery({
 *   queryKey: ['providers'],
 *   queryFn: fetchProviders,
 *   retry: false, // Won't retry on failure
 * })
 * ```
 *
 * Configuration inheritance order:
 * 1. Global defaults (defined below) are applied first
 * 2. Query-specific options override global settings
 * 3. Effective config = { ...globalDefaults, ...querySpecificOptions }
 *
 * ## Retry Configuration
 *
 * - **3 retry attempts** for failed queries (excluding 404s)
 * - **No retries for 404 errors** (resource not found is expected, not transient)
 * - **Exponential backoff**: 500ms → 1000ms → 2000ms between retries
 * - **Automatic logging**: When all retries are exhausted, error is logged and sent to Sentry
 * - **Mutations don't retry** by default (safer for data modification operations)
 *
 * ## Error Handling
 *
 * The QueryCache's onError callback is triggered when:
 * - A query fails after exhausting all retry attempts (fetchFailureCount >= 3)
 * - 404 errors are excluded from logging (expected behavior, not errors)
 * - This happens BEFORE the query's individual onError callback
 * - Useful for global error logging, monitoring, and alerting
 *
 * @example
 * ```typescript
 * // In your component
 * import { useQuery } from '@tanstack/react-query'
 *
 * function MyComponent() {
 *   // Automatically uses global retry: 3, retryDelay, etc.
 *   const { data, error, isPending } = useQuery({
 *     queryKey: ['myData'],
 *     queryFn: fetchMyData,
 *   })
 * }
 * ```
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const alarmIssueType = getTerminalErrorAlarmIssueType(query, error)
      if (alarmIssueType) {
        captureAlarm(error, {
          scope: 'query_cache',
          issueType: alarmIssueType,
          extra: { queryKey: query.queryKey, failureCount: query.state.fetchFailureCount },
        })
        return
      }

      const status = getErrorStatus(error)
      const hasExhaustedRetryAttempts = query.state.fetchFailureCount >= MAX_RETRY_ATTEMPTS

      if (hasExhaustedRetryAttempts && shouldRetryQuery(0, error)) {
        log.error(
          `Query failed after ${query.state.fetchFailureCount} retry attempts for queryKey: ${JSON.stringify(query.queryKey)}`,
          {
            queryKey: query.queryKey,
            errorMessage: error instanceof Error ? error.message : String(error),
            errorStatus: status,
            errorBody: (error as { body?: unknown })?.body,
            failureCount: query.state.fetchFailureCount,
            error, // Keep original error for stack trace
          }
        )
      }
    },
  }),
  defaultOptions: {
    queries: {
      gcTime: 60 * 1000 * 60,
      staleTime: 60 * 1000 * 10,
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
      // Exponential backoff: 500ms, 1000ms, 2000ms
      retryDelay: getQueryRetryDelay,
    },
    mutations: {
      // Mutations typically should not retry by default (they often modify data)
      // Individual mutations can override this if needed
      retry: false,
    },
  },
})

/**
 * Verify that the queryClient is properly initialized.
 * This is a runtime check that can be called to ensure the singleton exists.
 *
 * @returns true if queryClient is initialized, throws error otherwise
 * @throws Error if queryClient is not properly initialized
 *
 * @example
 * ```typescript
 * // In tests or early app initialization
 * ensureQueryClientInitialized()
 * ```
 */
export function ensureQueryClientInitialized(): boolean {
  if (!queryClient) {
    throw new Error('queryClient is not initialized. This should never happen.')
  }

  const defaultOptions = queryClient.getDefaultOptions()
  if (!defaultOptions.queries?.retry) {
    throw new Error('queryClient default options are not properly configured')
  }

  return true
}
